import React, { useState, useEffect } from 'react';

// Hooks
import { useSelector, useDispatch } from 'react-redux';

// Component
import ChangePasswordModal from './ChangePasswordModal';
import OlcButton from '~/components/General/OlcButton';

// Utils
import { MESSAGES } from '~/utils/message';
import request from '~/utils/request';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { PHONE_NO_REGEX } from '~/utils/constants';
import { formatPhoneNumber, handlePhoneNumberChange } from '~/utils/helperFunctions';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import { getUserData } from '~/redux/actions/auth-actions';
import { updateNotificationSettings } from '~/redux/actions/user-actions';

// MUI Components
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Skeleton,
  Switch,
  FormControlLabel,
  Checkbox,
  GlobalStyles,
} from '@mui/material';
import { IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// icons
import { CheckedCIcon, UncheckedCIcon } from '~/assets/images';

// styles
import './styles.scss';

const Account = () => {
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    paymentFails: false,
    orderIsMailed: false,
  });

  const user = useSelector((state) => state.userReducers?.user);
  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .test('not-only-spaces', 'First Name cannot contain only spaces', (value) => {
        if (!value) return true;
        return value.trim().length > 0;
      })
      .required(MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.FIRST_NAME_REQUIRED)
      .max(30, 'First Name must be less than 30 characters')
      .min(1, 'First Name is required'),
    lastName: yup
      .string()
      .test('not-only-spaces', 'Last Name cannot contain only spaces', (value) => {
        if (!value) return true;
        return value.trim().length > 0;
      })
      .required(MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.LAST_NAME_REQUIRED)
      .max(30, 'Last Name must be less than 30 characters')
      .min(1, 'Last Name is required'),
    phoneNo: yup
      .string()
      .matches(PHONE_NO_REGEX, MESSAGES.AUTH.INVALID_PHONE)
      .max(20, MESSAGES.SIGN_UP.PHONE_MAX_LENGTH)
      .required(MESSAGES.SIGN_UP.PHONE_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstName?.trim() || '',
      lastName: user?.lastName?.trim() || '',
      phoneNo: formatPhoneNumber(user?.phoneNo),
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  const handleEditClick = () => {
    setIsEditing(true);
    formik.setValues({
      firstName: user?.firstName?.trim() || '',
      lastName: user?.lastName?.trim() || '',
      phoneNo: formatPhoneNumber(user?.phoneNo),
    });
    resetValidation();
  };

  const handleCancel = () => {
    setIsEditing(false);
    formik.handleReset();
    resetValidation();
  };

  const handleClearField = (field) => {
    formik.setFieldValue(field, '');
    formik.setFieldTouched(field, true);
  };

  const handleNotificationChange = async (event) => {
    const { name, checked } = event.target;

    const updatedSettings = {
      ...notificationSettings,
      [name]: checked,
    };
    setNotificationSettings(updatedSettings);

    try {
      const payload = {
        paymentFails: updatedSettings.paymentFails,
        orderIsMailed: updatedSettings.orderIsMailed,
      };

      const response = await dispatch(updateNotificationSettings(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        setTimeout(() => {
          dispatch(getUserData());
        }, 2000);
      } else {
        dispatch(failure(response.data.message));
        setNotificationSettings(notificationSettings);
      }
    } catch (error) {
      dispatch(
        failure(error.response?.data?.message || error?.data?.message || MESSAGES.GENERAL_ERROR)
      );
      setNotificationSettings(notificationSettings);
    }
  };

  const handleSaveChanges = async () => {
    try {
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }

      if (formik?.values?.firstName?.trim() === '' || formik?.values?.lastName?.trim() === '') {
        return;
      }

      const payload = {
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        phoneNo: formik.values.phoneNo.trimStart(),
      };

      setLoading(true);
      const response = await request.post('users/update-info', payload);
      dispatch(success(response.data.message));

      // Show skeleton while refreshing data
      setIsRefreshingData(true);
      setIsEditing(false);
      await dispatch(getUserData());
      setIsRefreshingData(false);
    } catch (error) {
      dispatch(
        failure(error.response?.data?.message || error?.data?.message || MESSAGES.GENERAL_ERROR)
      );
      return error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNotificationSettings({
      paymentFails: user?.config?.notifications?.paymentFails || false,
      orderIsMailed: user?.config?.notifications?.orderIsMailed || false,
    });
  }, [user]);

  if (!user || !user.email) {
    return (
      <Box className="profileWrapper">
        <Box className="profileCard">
          {/* Header Skeleton */}
          <Box className="profileHeader">
            <Typography variant="h4" className="profileTitle">
              {MESSAGES.ACCOUNT.PROFILE.TITLE}
            </Typography>
            <Skeleton variant="rectangular" width={140} height={40} />
          </Box>

          {/* User Information Skeleton */}
          <Box className="userInfoSection">
            <Box className="infoItem">
              <Typography className="infoLabel">{MESSAGES.ACCOUNT.PROFILE.NAME_LABEL}</Typography>
              <Skeleton variant="text" width={300} height={23} />
            </Box>

            <Box className="infoItem">
              <Typography className="infoLabel">{MESSAGES.ACCOUNT.PROFILE.PHONE_LABEL}</Typography>
              <Skeleton variant="text" width={200} height={23} />
            </Box>

            <Box className="infoItem">
              <Typography className="infoLabel">{MESSAGES.ACCOUNT.PROFILE.EMAIL_LABEL}</Typography>
              <Skeleton variant="text" width={250} height={23} />
            </Box>
          </Box>

          {/* Change Password Button Skeleton */}
          <Box className="changePasswordSection">
            <Skeleton variant="rectangular" width={170} height={40} />
          </Box>
        </Box>
        <Box className="profileCard">
          <Box className="profileHeader notificationContent">
            <Typography variant="h4" className="profileTitle">
              {MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.TITLE}
            </Typography>
            <Typography variant="body1" className="profileSubtitle">
              {MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.SUBTITLE}
            </Typography>
            <Box className="notificationCheckboxContainer">
              <Skeleton variant="rectangular" width={250} height={25} />
              <Skeleton variant="rectangular" width={250} height={25} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="profileWrapper">
        <Box className="profileCard">
          {/* Header */}
          <Box className="profileHeader">
            <Typography variant="h4" className="profileTitle">
              {MESSAGES.ACCOUNT.PROFILE.TITLE}
            </Typography>
            {!isEditing ? (
              <OlcButton
                variant="secondary"
                className="editBtn"
                onClick={handleEditClick}
                disabled={loading || isRefreshingData}
              >
                {MESSAGES.ACCOUNT.PROFILE.EDIT_BTN_TEXT}
              </OlcButton>
            ) : (
              <Box className="actionButtons">
                <OlcButton variant="secondary" className="cancelButton" onClick={handleCancel}>
                  Cancel
                </OlcButton>
                <OlcButton
                  variant="primary"
                  className="saveButton"
                  onClick={handleSaveChanges}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      sx={{
                        color: 'white',
                        width: '20px !important',
                        height: '20px !important',
                      }}
                    />
                  ) : (
                    'Save Changes'
                  )}
                </OlcButton>
              </Box>
            )}
          </Box>

          {/* User Information */}
          <Box className="userInfoSection">
            <Box className="infoItem">
              <Typography className="infoLabel">
                {isEditing
                  ? MESSAGES.ACCOUNT.PROFILE.FIRST_NAME_LABEL
                  : MESSAGES.ACCOUNT.PROFILE.FULL_NAME_LABEL}
                {isEditing && <span className="olcRequired">*</span>}
              </Typography>
              {isRefreshingData ? (
                <Skeleton variant="text" width={300} height={23} />
              ) : !isEditing ? (
                <Typography className="infoValue">{user?.fullName}</Typography>
              ) : (
                <Box className="nameInputs">
                  <TextField
                    placeholder="John"
                    sx={{
                      width: '347px',
                    }}
                    name="firstName"
                    onChange={formik.handleChange}
                    className={
                      formik.errors.firstName && formik.touched.firstName
                        ? 'inputField invalid'
                        : 'inputField'
                    }
                    disabled={loading}
                    focused={loading ? false : true}
                    onBlur={realTimeValidation ? formik.handleBlur : undefined}
                    onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
                    value={formik.values.firstName}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                          {formik.values.firstName && (
                            <IconButton
                              className="cpClearInputBtn"
                              size="small"
                              onClick={() => handleClearField('firstName')}
                              edge="end"
                              sx={{ padding: '4px' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
              {isEditing && formik.touched.firstName && formik.errors.firstName && (
                <Typography className="errorMessage">{formik.errors.firstName}</Typography>
              )}
            </Box>
            {isEditing && (
              <Box className="infoItem">
                <Typography className="infoLabel">
                  {MESSAGES.ACCOUNT.PROFILE.LAST_NAME_LABEL}
                  <span className="olcRequired">*</span>
                </Typography>
                <Box className="nameInputs">
                  <TextField
                    placeholder="Doe"
                    sx={{
                      width: '347px',
                    }}
                    name="lastName"
                    onChange={formik.handleChange}
                    className={
                      formik.errors.lastName && formik.touched.lastName
                        ? 'inputField invalid'
                        : 'inputField'
                    }
                    disabled={loading}
                    focused={loading ? false : true}
                    onBlur={realTimeValidation ? formik.handleBlur : undefined}
                    onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
                    value={formik.values.lastName}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                          {formik.values.lastName && (
                            <IconButton
                              className="cpClearInputBtn"
                              size="small"
                              onClick={() => handleClearField('lastName')}
                              edge="end"
                              sx={{ padding: '4px' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {isEditing && formik.touched.lastName && formik.errors.lastName && (
                  <Typography className="errorMessage">{formik.errors.lastName}</Typography>
                )}
              </Box>
            )}

            <Box className="infoItem">
              <Typography className="infoLabel">
                {MESSAGES.ACCOUNT.PROFILE.PHONE_LABEL}
                {isEditing && <span className="olcRequired">*</span>}
              </Typography>
              {isRefreshingData ? (
                <Skeleton variant="text" width={200} height={23} />
              ) : !isEditing ? (
                <Typography className="infoValue">
                  {formatPhoneNumber(user?.phoneNo) || '-'}
                </Typography>
              ) : (
                <TextField
                  placeholder={MESSAGES.SIGN_UP.PHONE_PLACEHOLDER}
                  sx={{
                    width: '347px',
                  }}
                  name="phoneNo"
                  onChange={(event) =>
                    handlePhoneNumberChange(event.target.value, formik.setFieldValue, 'phoneNo')
                  }
                  className={
                    formik.errors.phoneNo && formik.touched.phoneNo
                      ? 'inputField invalid'
                      : 'inputField'
                  }
                  disabled={loading}
                  focused={loading ? false : true}
                  onBlur={realTimeValidation ? formik.handleBlur : undefined}
                  onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
                  value={formik.values.phoneNo.trimStart()}
                  inputProps={{ maxLength: 14 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                        {formik.values.phoneNo?.trim() && (
                          <IconButton
                            className="cpClearInputBtn"
                            size="small"
                            onClick={() => handleClearField('phoneNo')}
                            edge="end"
                            sx={{ padding: '4px' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {isEditing && formik.touched.phoneNo && formik.errors.phoneNo && (
                <Typography className="errorMessage">{formik.errors.phoneNo}</Typography>
              )}
            </Box>

            <Box className="infoItem">
              <Typography className="infoLabel">{MESSAGES.ACCOUNT.PROFILE.EMAIL_LABEL}</Typography>
              {isRefreshingData ? (
                <Skeleton variant="text" width={250} height={23} />
              ) : (
                <Typography className="infoValue">{user?.email}</Typography>
              )}
            </Box>
          </Box>

          {/* Change Password Button */}
          {!isEditing && (
            <Box className="changePasswordSection">
              <OlcButton
                variant="primary"
                className="changePasswordButton"
                onClick={() => setChangePasswordModal(true)}
              >
                {MESSAGES.ACCOUNT.PROFILE.CHANGE_PASSWORD_BTN_TEXT}
              </OlcButton>
            </Box>
          )}
        </Box>
        {/* <Box className="profileCard">
          <Box className="profileHeader notificationContent">
            <Typography variant="h4" className="profileTitle">
              {MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.TITLE}
            </Typography>
            <Typography variant="body1" className="profileSubtitle">
              {MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.SUBTITLE}
            </Typography>
            <Box className="notificationCheckboxContainer">
              <FormControlLabel
                control={
                  <Checkbox
                    checkedIcon={<img src={CheckedCIcon} alt="check" />}
                    icon={<img src={UncheckedCIcon} alt="check" />}
                    onChange={handleNotificationChange}
                    checked={notificationSettings?.orderIsMailed}
                    name="orderIsMailed"
                  />
                }
                label={MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.NOTIFY_WHEN_ORDER_IS_MAILED}
                classes={{
                  label: 'notificationCheckboxLabel',
                  root: 'notificationCheckboxRoot',
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checkedIcon={<img src={CheckedCIcon} alt="check" />}
                    icon={<img src={UncheckedCIcon} alt="check" />}
                    onChange={handleNotificationChange}
                    checked={notificationSettings?.paymentFails}
                    name="paymentFails"
                  />
                }
                label={MESSAGES.ACCOUNT.PROFILE.NOTIFICATIONS.NOTIFY_WHEN_PAYMENT_FAILS}
                classes={{
                  label: 'notificationCheckboxLabel',
                  root: 'notificationCheckboxRoot',
                }}
              />
            </Box>
          </Box>
        </Box> */}
      </Box>

      <ChangePasswordModal
        openModal={changePasswordModal}
        handleCloseModal={() => setChangePasswordModal(false)}
      />
    </>
  );
};

export default Account;
